/* eslint-disable react/prop-types */
import React, { Children, isValidElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/design-system/molecules/header';
import { useAuthContext } from '@/hooks/use-auth';
import { COLOC_APP_API_HOST, COLOC_MAIN_APP } from '@/config';
import { useCitiesListContext } from '@/features/marketplace/hooks/use-get-cities';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { ConnectedMobileSideMenuPropsAndSubComponents, Slots } from './types';

import { useLocale } from '@/hooks/use-locale';
import { LangOption } from '@/types/global';
import { ReactSVG } from 'react-svg';
import { CityListItem } from '@/features/marketplace/components/city-list-item';
import { getCityUrl } from '@/features/marketplace/utils/city-navigation';

const Leading: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="flex w-full flex-col items-center justify-center">
      <ReactSVG
        src={require(`@assets/images/logo-mobile-sidebar.svg`)}
        aria-hidden="true"
        className="mb-6 inline-block align-baseline"
        beforeInjection={(svg) => {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('width', `100`);
          svg.setAttribute('height', `60`);
        }}
      />
      {children}
    </div>
  );
};

const Trailing: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const ConnectedMobileSideMenu: ConnectedMobileSideMenuPropsAndSubComponents = ({ children }) => {
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const { citiesList } = useCitiesListContext();
  const [isCitiesOpen, setIsCitiesOpen] = useState(false);
  const { currentLocale, setLocale } = useLocale();

  const changeLanguage = (lng: LangOption) => {
    setLocale(lng);
  };

  const filledSlots = useMemo(() => {
    const slots: Slots = {
      leading: null,
    };

    Children.toArray(children).forEach((child) => {
      if (!isValidElement(child)) {
        return;
      }

      switch (child.type) {
        case Leading:
          slots.leading = child;
          break;
        default:
      }
    });

    return slots;
  }, [children]);

  return (
    <Header.MobileSideMenu>
      <Header.MobileSideMenu.Leading>
        <div>
          {filledSlots.leading}
          <div className="pt-3"></div>
          <Header.MobileSideMenu.MobileSideMenuOptions>
            <Header.MobileSideMenu.MobileSideMenuOption
              as="button"
              type="button"
              onClick={() => setIsCitiesOpen(!isCitiesOpen)}
              className="mb-1"
            >
              <span className="flex w-full items-center gap-1 text-[16px] font-bold text-text-primaryHeavy">
                {t('js.features.shared.header.cities')}
                <IconSaxIcon
                  name={isCitiesOpen ? 'arrow-up-1' : 'arrow-down'}
                  size={18}
                  className="stroke-2"
                />
              </span>
            </Header.MobileSideMenu.MobileSideMenuOption>

            {isCitiesOpen && [
              <Header.MobileSideMenu.MobileSideMenuOption
                key="all-cities"
                href={`${COLOC_MAIN_APP}/${currentLocale}/shared-houses`}
              >
                <div className="border-b-[1px] border-solid border-E50 pb-2">
                  <div className="flex w-full items-center">
                    <div className="mb-1 mr-[10px] flex h-[48px] min-w-[48px] items-center justify-center rounded-[10px] bg-bg-primaryMedium">
                      <ReactSVG
                        src={require('@assets/icons/home-page-info-icons/building-office-2.svg')}
                        beforeInjection={(svg) => {
                          svg.setAttribute('width', '24');
                          svg.setAttribute('height', '24');
                        }}
                      />
                    </div>
                    <div className="relative flex w-full flex-col">
                      <span className="text-[16px] font-bold leading-[2.4rem] text-text-secondary">
                        {t('js.features.shared.header.all_cities')}
                      </span>
                      <span className="text-[12px] font-medium text-text-primaryMedium">
                        {t('js.features.shared.header.all_cities_subtitle')}
                      </span>
                      <span className="absolute right-0 top-1/2 -translate-y-1/2">
                        <ReactSVG
                          src={require('@assets/images/marketplace/lighting_active.svg')}
                          className="searchbar-item-available-immediately"
                          beforeInjection={(svg) => {
                            svg.setAttribute('width', '18');
                            svg.setAttribute('height', '18');
                          }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </Header.MobileSideMenu.MobileSideMenuOption>,
               ...citiesList.map((city) => (
                 <Header.MobileSideMenu.MobileSideMenuOption
                   key={city.id}
                   href={getCityUrl(city, currentLocale)}
                 >
                   <div className="border-b-[1px] border-solid border-E50 pb-2">
                     <div className="flex w-full items-center">
                       <CityListItem
                         city={city}
                         imgSize={48}
                         imgClassName="mb-1 mr-[10px] min-w-[48px]"
                         textContainerClassName="relative flex w-full flex-col"
                         nameClassName="text-[16px] font-bold leading-[1.4] text-text-secondary"
                       />
                     </div>
                   </div>
                 </Header.MobileSideMenu.MobileSideMenuOption>
               )),
            ]}
            <Header.MobileSideMenu.MobileSideMenuOption href="https://help.colochousing.com/">
              {t('js.features.shared.header.contact_us')}
            </Header.MobileSideMenu.MobileSideMenuOption>
          </Header.MobileSideMenu.MobileSideMenuOptions>
          <div className="pt-3"></div>
          <Header.MobileSideMenu.MobileSideMenuOptions>
            {user ? (
              <form method="post" action={`${COLOC_APP_API_HOST}/users/sign_out`}>
                <input type="hidden" name="_method" value="delete" />
                <Header.MobileSideMenu.MobileSideMenuOption
                  as="button"
                  type="submit"
                  iconName="logout"
                  destructive
                  className="flex border-b-[1px] border-t-[1px] border-solid border-E50"
                >
                  {t('js.features.shared.header.sign_out')}
                </Header.MobileSideMenu.MobileSideMenuOption>
              </form>
            ) : (
              <Header.MobileSideMenu.MobileSideMenuOption
                href={`${COLOC_APP_API_HOST}/users/sign_in`}
                className="max-h-auto border-b-[1px] border-t-[1px] border-solid border-E50 py-4"
              >
                {t('js.features.shared.header.login')}
              </Header.MobileSideMenu.MobileSideMenuOption>
            )}
          </Header.MobileSideMenu.MobileSideMenuOptions>

          <div className="pt-4" />

          <Header.MobileSideMenu.MobileSideMenuOptions>
            <Header.MobileSideMenu.MobileSideMenuOption
              as="button"
              onClick={() => changeLanguage('en')}
            >
              <div className="flex items-center">
                <img
                  src={require('@assets/images/uk-flag.svg')}
                  alt="English"
                  className="mr-2 h-4 rounded-full"
                />
                <span>{t('js.features.shared.header.english')}</span>
              </div>
            </Header.MobileSideMenu.MobileSideMenuOption>
            <Header.MobileSideMenu.MobileSideMenuOption
              as="button"
              onClick={() => changeLanguage('nl')}
            >
              <div className="flex items-center">
                <img
                  src={require('@assets/images/dutch-flag.svg')}
                  alt="Dutch"
                  className="mr-2 h-4 rounded-full"
                />
                <span>{t('js.features.shared.header.dutch')}</span>
              </div>
            </Header.MobileSideMenu.MobileSideMenuOption>
            <Header.MobileSideMenu.MobileSideMenuOption
              as="button"
              onClick={() => changeLanguage('fr')}
            >
              <div className="flex items-center">
                <img
                  src={require('@assets/images/french-flag.svg')}
                  alt="French"
                  className="mr-2 h-4 rounded-full"
                />
                <span>{t('js.features.shared.header.french')}</span>
              </div>
            </Header.MobileSideMenu.MobileSideMenuOption>
          </Header.MobileSideMenu.MobileSideMenuOptions>
        </div>
      </Header.MobileSideMenu.Leading>
    </Header.MobileSideMenu>
  );
};

ConnectedMobileSideMenu.Leading = Leading;
ConnectedMobileSideMenu.Trailing = Trailing;

export { ConnectedMobileSideMenu };
